'use client'

import * as React from 'react'
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { X } from 'lucide-react'

import cn from '@config/tailwind/cn'

import { typographyVariants } from '../lib'
import { Button } from './button'

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot='dialog' {...props} />
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot='dialog-trigger' {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot='dialog-portal' {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot='dialog-close' {...props} />
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot='dialog-overlay'
      className={cn(
        'ui:data-open:animate-in ui:data-closed:animate-out ui:data-closed:fade-out-0 ui:data-open:fade-in-0',
        'ui:bg-neutral-black/30 ui:backdrop-blur-sm ui:duration-100',
        'ui:fixed ui:inset-0 ui:isolate ui:z-50',
        className,
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  closeButtonClassName,
  iconButtonClassName,
  keepMounted,
  overlayClassName,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean
  closeButtonClassName?: string
  iconButtonClassName?: string
  /** Keep portal mounted when closed (e.g. for use with React Activity to preserve state) */
  keepMounted?: boolean
  /** Optional class for the backdrop (e.g. higher z-index when dialog is used inside fullscreen reader) */
  overlayClassName?: string
}) {
  return (
    <DialogPortal keepMounted={keepMounted}>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Popup
        data-slot='dialog-content'
        className={cn(
          'ui:data-open:animate-in ui:data-closed:animate-out ui:data-closed:fade-out-0 ui:data-open:fade-in-0',
          'ui:data-closed:zoom-out-95 ui:data-open:zoom-in-95',
          'ui:bg-neutral-white ui:ring-grey-20 ui:ring-1',
          'ui:fixed ui:top-1/2 ui:left-1/2 ui:z-50 ui:w-full ui:max-w-[1100px] ui:max-h-[95dvh]',
          'ui:-translate-x-1/2 ui:-translate-y-1/2',
          'ui:rounded-[28px] ui:shadow-xl ui:p-6',
          // `overflow-x-hidden` pairs with `overflow-y-auto` on purpose: per CSS, a `visible` axis
          // next to a scrolling one computes to `auto`, so without this the popup is horizontally
          // scrollable and any child that pokes past the padding box gets a scrollbar. A filled
          // `InputOTP` does exactly that — input-otp puts `width: calc(100% + 40px)` on its hidden
          // input. `AlertDialogContent` carries the same guard.
          'ui:overflow-x-hidden ui:overflow-y-auto ui:outline-hidden ui:duration-100',
          'ui:grid ui:gap-4 ui:text-sm',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot='dialog-close'
            render={
              <Button
                variant='ghost'
                size='icon'
                className={cn('ui:absolute ui:top-4 ui:right-4', closeButtonClassName)}
              >
                <X className={cn('ui:w-5 ui:h-5 ui:text-blue-100', iconButtonClassName)} />
                <span className='ui:sr-only'>Close</span>
              </Button>
            }
          />
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot='dialog-header' className={cn('ui:flex ui:flex-col ui:gap-2', className)} {...props} />
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot='dialog-footer'
      className={cn(
        'ui:bg-grey-10/50 ui:-mx-6 ui:-mb-6 ui:rounded-b-[28px] ui:border-t ui:border-outline ui:p-4',
        'ui:flex ui:flex-col-reverse ui:gap-2 ui:sm:flex-row ui:sm:justify-end',
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && <DialogPrimitive.Close render={<Button variant='outline'>Close</Button>} />}
    </div>
  )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot='dialog-title'
      className={cn(
        'ui:text-base ui:leading-none ui:font-medium ui:text-neutral-black',
        typographyVariants({ variant: 'headline-sm' }),
        className,
      )}
      {...props}
    />
  )
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot='dialog-description'
      className={cn(
        'ui:text-grey-100 ui:text-sm',
        'ui:*:[a]:hover:text-blue-100 ui:*:[a]:underline ui:*:[a]:underline-offset-3',
        typographyVariants({ variant: 'body-md' }),
        className,
      )}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
